import { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, ErrorNote, RemoveButton } from '../components/ui';
import ShareButton from '../components/ShareButton';
import PdfPreview from '../components/PdfPreview';
import PhotoPicker from '../components/PhotoPicker';
import { money, composeAddress, normSection, proposalTotals, legacyTotal } from '../lib/format';

// Matches MAX_PHOTOS in src/routes/proposals.js.
const MAX_PHOTOS = 12;

/** Server photos arrive as `photos` (loadable URLs) alongside `photoRefs` (what's stored). */
const photosFrom = (p) => (p?.photos || []).map((url, i) => ({ url, ref: p.photoRefs?.[i] }));

/** A textarea that grows to fit its text, so long scope paragraphs stay readable. */
function GrowingTextarea(props) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 2 + 'px';
  }, [props.value]);
  return <textarea ref={ref} {...props} />;
}

export default function ProposalBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, run } = useApp();

  const { data, error, loading, reload } = useAsync(
    () => Promise.all([api.get('/projects/' + id), api.get('/settings')]), [id],
  );

  const [proposal, setProposal] = useState(null);
  const [sections, setSections] = useState([]);
  const [taxPct, setTaxPct] = useState(0);
  const [jobLocation, setJobLocation] = useState('');
  const [text, setText] = useState({ opNotes: '', warranty: '', pricingNotes: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);            // [{ url, ref? }]
  const [agreement, setAgreement] = useState(null);
  const [agr, setAgr] = useState({ terms: '', payment: '', exclusions: '' });
  const [sendTo, setSendTo] = useState('');
  const [extraTo, setExtraTo] = useState('');
  const [preview, setPreview] = useState(null);        // { docs, index }

  useEffect(() => {
    if (!data) return;
    const [p, settings] = data;
    const existing = p.proposal;
    setProposal(existing || null);
    setSections(existing?.sections?.length
      ? existing.sections.map(normSection)
      : [{ name: 'Scope of Work', items: [{ description: p.siteVisit?.scopeNotes || '', amount: 0 }] }]);
    setTaxPct(existing?.taxPct != null ? Number(existing.taxPct) : 0);
    setJobLocation(existing?.jobLocation || composeAddress(p.customer) || '');
    setText({
      opNotes: existing?.opNotesText ?? settings.defaultOperationalNotes ?? '',
      warranty: existing?.warrantyText ?? settings.defaultWarrantyText ?? '',
      pricingNotes: existing?.pricingNotesText ?? settings.defaultPricingNotes ?? '',
      notes: existing?.notesText ?? '',
    });
    setPhotos(photosFrom(existing));
    const a = p.agreement;
    setAgreement(a || null);
    setAgr({
      terms: a?.termsText || settings.defaultTerms || '',
      payment: a?.paymentScheduleText || `${settings.defaultDepositPct}% deposit on signing, balance on completion.`,
      exclusions: a?.exclusions || '',
    });
    setSendTo(p.customer?.email || '');
  }, [data]);

  // Totals derive from state rather than being pushed into the DOM by hand,
  // so the displayed figure and the saved figure can't drift apart.
  const totals = useMemo(
    () => proposalTotals(sections, taxPct, legacyTotal(proposal)),
    [sections, taxPct, proposal],
  );

  if (loading) return <Spinner label="Loading proposal" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;
  const [project] = data;

  const patchSection = (si, patch) => setSections(s => s.map((x, i) => i === si ? { ...x, ...patch } : x));
  const patchItem = (si, ii, patch) => setSections(s => s.map((sec, i) => i !== si ? sec : {
    ...sec, items: sec.items.map((it, j) => j === ii ? { ...it, ...patch } : it),
  }));

  const body = () => ({
    sections, total: totals.total, taxPct,
    jobLocation,
    opNotesText: text.opNotes, warrantyText: text.warranty,
    notesText: text.notes, pricingNotesText: text.pricingNotes,
    // An existing photo goes back as its stored ref; a new one as its data URL.
    photos: photos.map(p => p.ref || p.url),
  });

  // The proposal and its agreement are one step: saving writes both, so the
  // rep never has to open a second screen just to send the terms along.
  // A signed agreement is a legal record and is never rewritten from here.
  async function saveAll() {
    const saved = proposal
      ? await api.put('/proposals/' + proposal.id, body())
      : await api.post('/proposals', { projectId: id, ...body() });
    setProposal(saved);
    setPhotos(photosFrom(saved));
    if (agreement?.status !== 'signed') {
      const abody = { termsText: agr.terms, paymentScheduleText: agr.payment, exclusions: agr.exclusions };
      const a = agreement
        ? await api.put('/agreements/' + agreement.id, abody)
        : await api.post('/agreements', { projectId: id, ...abody });
      setAgreement(a);
      return { saved, agreement: a };
    }
    return { saved, agreement };
  }

  const guarded = (fn) => run(async () => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  });

  const onSave = () => guarded(async () => { await saveAll(); toast('Saved'); });

  const onPreview = () => guarded(async () => {
    const { saved } = await saveAll();
    setPreview({ docs: [{ label: 'Proposal', url: `/api/p/${saved.token}/pdf` }], index: 0 });
  });

  const onSend = () => guarded(async () => {
    const { saved } = await saveAll();
    const r = await api.post(`/proposals/${saved.id}/send`, { to: sendTo.trim() || undefined, cc: extraTo });
    navigator.clipboard?.writeText(`${location.origin}/p/${r.proposal.token}`).catch(() => {});
    const failed = [r.email, ...(r.extraEmails || [])].filter(e => e && !e.delivered);
    if (!r.email) toast('Link copied — no email address, so nothing was sent');
    else if (failed.length) toast(`Link copied, but ${failed.length} email${failed.length > 1 ? 's' : ''} failed: ${failed[0].error || 'unknown error'}`);
    else toast(`Proposal sent${r.extraEmails?.length ? ` to ${1 + r.extraEmails.length} people` : ''} — link copied`);
    navigate(project.standalone ? '/documents' : `/project/${id}`);
  });

  // For after the customer already has it: copies go out without changing status.
  const onShare = () => guarded(async () => {
    const { saved } = await saveAll();
    const r = await api.post(`/proposals/${saved.id}/share`, { recipients: extraTo });
    const failed = r.emails.filter(e => !e.delivered);
    toast(failed.length ? `${failed.length} email${failed.length > 1 ? 's' : ''} failed: ${failed[0].error || 'unknown error'}` : `Sent to ${r.emails.length} ${r.emails.length === 1 ? 'person' : 'people'}`);
    setExtraTo('');
  });

  return (
    <>
      <PageHeader
        title="Proposal"
        sub={`${project.number} · ${project.customer?.name || ''}`}
        back={project.standalone ? '/documents' : `/project/${id}`}
        action={<button className="act" onClick={onSave} disabled={busy}>Save</button>}
      />
      <div className="view">
        <Card>
          <SectionTitle>Header</SectionTitle>
          <div style={{ marginTop: 'var(--s-3)' }}>
            <Field label="Job location" id="loc">
              <input id="loc" className="fi" value={jobLocation} onChange={e => setJobLocation(e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card>
          <SectionTitle sub='Group items by heading. Enter an amount on priced lines; leave blank for inclusion notes.'>
            Scope &amp; line items
          </SectionTitle>
          <div className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
            {sections.map((s, si) => (
              <div className="scope-section" key={si}>
                <div className="head">
                  <input value={s.name} placeholder="Section heading (e.g. Roofing)"
                    aria-label={`Section ${si + 1} heading`}
                    onChange={e => patchSection(si, { name: e.target.value })} />
                  <RemoveButton label={`Remove section ${s.name || si + 1}`}
                    onClick={() => setSections(x => x.filter((_, i) => i !== si))} />
                </div>
                {s.items.map((it, ii) => (
                  <div className="scope-item" key={ii}>
                    <GrowingTextarea rows={1} placeholder="Item description…"
                      aria-label={`Item ${ii + 1} description`}
                      value={it.description}
                      onChange={e => patchItem(si, ii, { description: e.target.value })} />
                    <span className="amt">
                      <input type="number" min="0" step="0.01" placeholder="0"
                        aria-label={`Item ${ii + 1} amount`}
                        value={it.amount || ''}
                        onChange={e => patchItem(si, ii, { amount: parseFloat(e.target.value || '0') })} />
                    </span>
                    <RemoveButton label={`Remove item ${ii + 1}`}
                      onClick={() => patchSection(si, { items: s.items.filter((_, j) => j !== ii) })} />
                  </div>
                ))}
                <button type="button" className="btn sm" style={{ marginTop: 'var(--s-3)' }}
                  onClick={() => patchSection(si, { items: [...s.items, { description: '', amount: 0 }] })}>
                  ＋ Add line item
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="btn" style={{ marginTop: 'var(--s-3)' }}
            onClick={() => setSections(s => [...s, { name: 'New section', items: [{ description: '', amount: 0 }] }])}>
            ＋ Add section
          </button>
        </Card>

        <Card>
          <SectionTitle sub="Shown on the proposal and included in the PDF. Photos are resized to keep the email small.">
            Photos
          </SectionTitle>
          <PhotoPicker photos={photos} onChange={setPhotos} max={MAX_PHOTOS} />
        </Card>

        <div className="totals-card">
          <div className="totals-row">
            <span className="lbl">Subtotal</span>
            <span className="val">{money(totals.subtotal)}</span>
          </div>
          <div className="totals-row">
            <span className="lbl"><label htmlFor="tax">Tax</label></span>
            <span className="tax-input">
              <input id="tax" type="number" min="0" step="0.01" value={taxPct}
                onChange={e => setTaxPct(parseFloat(e.target.value || '0'))} />
              <span aria-hidden="true">%</span>
              <span className="val">{money(totals.tax)}</span>
            </span>
          </div>
          <div className="totals-divider" />
          <div className="totals-row grand">
            <span className="lbl">Total</span>
            <span className="val">{money(totals.total)}</span>
          </div>
        </div>

        <div className="split">
          <Card>
            <SectionTitle sub="Shown on the proposal before the total.">Standard operational notes</SectionTitle>
            <textarea className="fi" rows={5} style={{ marginTop: 'var(--s-3)' }} aria-label="Standard operational notes"
              value={text.opNotes} onChange={e => setText(t => ({ ...t, opNotes: e.target.value }))} />
          </Card>
          <Card>
            <SectionTitle sub="Common wording used on every proposal.">Warranty</SectionTitle>
            <textarea className="fi" rows={5} style={{ marginTop: 'var(--s-3)' }} aria-label="Warranty text"
              value={text.warranty} onChange={e => setText(t => ({ ...t, warranty: e.target.value }))} />
          </Card>
          <Card>
            <SectionTitle>Pricing notes</SectionTitle>
            <textarea className="fi" rows={4} style={{ marginTop: 'var(--s-3)' }} aria-label="Pricing notes"
              placeholder="e.g. price good for 60 days, deposit terms…"
              value={text.pricingNotes} onChange={e => setText(t => ({ ...t, pricingNotes: e.target.value }))} />
          </Card>
          <Card>
            <SectionTitle>Notes</SectionTitle>
            <textarea className="fi" rows={4} style={{ marginTop: 'var(--s-3)' }} aria-label="Notes"
              placeholder="Anything else for this customer…"
              value={text.notes} onChange={e => setText(t => ({ ...t, notes: e.target.value }))} />
          </Card>
        </div>

        <Card>
          <SectionTitle sub="Printed at the end of the proposal. The customer signs once to accept both.">
            Terms &amp; signing
          </SectionTitle>
          {agreement?.status === 'signed' ? (
            <p className="note ok" style={{ marginTop: 'var(--s-3)' }}>This agreement is already signed and can’t be edited here.</p>
          ) : null}
          <div className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
            <Field label="Terms & conditions" id="agr-terms">
              <textarea id="agr-terms" className="fi" rows={6} disabled={agreement?.status === 'signed'}
                value={agr.terms} onChange={e => setAgr(a => ({ ...a, terms: e.target.value }))} />
            </Field>
            <div className="split">
              <Field label="Payment schedule" id="agr-pay">
                <textarea id="agr-pay" className="fi" rows={3} disabled={agreement?.status === 'signed'}
                  value={agr.payment} onChange={e => setAgr(a => ({ ...a, payment: e.target.value }))} />
              </Field>
              <Field label="Exclusions" id="agr-excl">
                <textarea id="agr-excl" className="fi" rows={3} disabled={agreement?.status === 'signed'}
                  placeholder="Anything explicitly not covered…"
                  value={agr.exclusions} onChange={e => setAgr(a => ({ ...a, exclusions: e.target.value }))} />
              </Field>
            </div>
          </div>
        </Card>

        <Card>
          <SectionTitle sub="The customer gets one PDF and one link to review and sign the proposal.">
            Send
          </SectionTitle>
          <div className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
            <Field label="Customer email" id="send-to" hint={sendTo ? undefined : 'No email on file — add one, or the link will just be copied.'}>
              <input id="send-to" className="fi" type="email" inputMode="email" value={sendTo}
                onChange={e => setSendTo(e.target.value)} />
            </Field>
            <Field label="Also email to (people outside the company)" id="send-cc"
              hint="Optional — e.g. an insurer or property manager. Separate addresses with commas; each gets their own copy. Team members can’t be added here.">
              <input id="send-cc" className="fi" type="text" inputMode="email" placeholder="name@example.com, other@example.com"
                value={extraTo} onChange={e => setExtraTo(e.target.value)} />
            </Field>
          </div>
        </Card>

        <div className="btn-row">
          <button className="btn" onClick={onPreview} disabled={busy}>Save &amp; preview</button>
          {proposal && proposal.status !== 'draft' ? (
            <button className="btn" onClick={onShare} disabled={busy || !extraTo.trim()}>Email a copy to others</button>
          ) : null}
          <button className="btn pri" onClick={onSend} disabled={busy}>Send proposal</button>
        </div>

        {/*
          Only offered once the proposal has been saved and has a token — the
          PDF is fetched by token, so there is nothing to share before that.
        */}
        {proposal?.token ? (
          <Card>
            <SectionTitle sub="The same PDF that gets attached to the customer's email.">
              This proposal as a document
            </SectionTitle>
            <div style={{ marginTop: 'var(--s-3)' }}>
              <ShareButton
                onOpen={() => setPreview({ docs: [{ label: 'Proposal', url: `/api/p/${proposal.token}/pdf` }], index: 0 })}
                pdfUrl={`/api/p/${proposal.token}/pdf`}
                publicUrl={`${location.origin}/p/${proposal.token}`}
                filename={`Proposal-${proposal.number}.pdf`}
              />
            </div>
          </Card>
        ) : null}
      </div>
      {preview ? (
        <PdfPreview docs={preview.docs} index={preview.index}
          onIndex={(index) => setPreview(p => ({ ...p, index }))}
          onClose={() => setPreview(null)} />
      ) : null}
    </>
  );
}
